import styled, { DefaultTheme } from 'styled-components'
import { breakpoints } from '../assets/styles/mediaBreakpoints'
import { PillButton } from '../components/common/stand_alone/pillButton'

const ResearchPage = () => {
  return (
    <ResearchSection>
      <FirstResearchSection>
        <ResearchSectionContainer>
          <ResearchTitle>Student Research Repository</ResearchTitle>
          <p>
            A space for graduate students to share their research for a nominal
            fee, contributing plain-language lessons and challenges that align
            with sustainability goals. This initiative builds a repository of
            actionable solutions while supporting emerging researchers.
          </p>
        </ResearchSectionContainer>
        <ResearchBlockContainer>
          <ResearchBlocks>
            <ResearchImage
              src="/images/research/research.png"
              alt="Research Image"
            />
            <ResearchBlock
              title="Community Discussions"
              text="Connect with like-minded individuals in our discussion forums. Share ideas, collaborate on solutions, and drive change together."
              bg="secondary-600"
              titleColor="#BBE148"
              textColor="white"
              titleSize="h3"
            />
          </ResearchBlocks>
          <ResearchBlocks>
            <ResearchBlock
              title="Search and Filter Tools"
              text="Looking for specific research? Use our powerful search bar and filters to browse by topic, region, or SDG focus. Find exactly what you need, faster."
              bg="secondary-300"
              titleColor="white"
              textColor="white"
              titleSize="h3"
            />
            <ResearchBlock
              title="Research Categories"
              text="Navigate through our repository by topic to find precisely what you need."
              bg="primary-500"
              titleColor="black"
              textColor="black"
              titleSize="h1"
              grow={true}
            />
          </ResearchBlocks>
        </ResearchBlockContainer>
      </FirstResearchSection>
      <SecondResearchSection>
        <ResearchSectionContainer>
          <ResearchTitle>
            We need more actions to protect our planet
          </ResearchTitle>
          <p>
            Curious about our mission, vision, and the work we do? Learn more
            about the Community of Guardians and how we’re making an impact.
          </p>
          <PillButton title="Our Story" link="/about" />
        </ResearchSectionContainer>
      </SecondResearchSection>
    </ResearchSection>
  )
}

const ResearchBlock = ({
  title,
  bg,
  titleColor,
  text,
  textColor,
  titleSize,
  grow = false,
}: {
  title: string
  bg: keyof DefaultTheme['colors']
  titleColor: string
  text: string
  textColor: string
  titleSize: string
  grow?: boolean
}) => {
  return (
    <ResearchBlockStyled $bg={bg} $grow={grow}>
      <ResearchBlockTitle $titleColor={titleColor} $titleSize={titleSize}>
        {title}
      </ResearchBlockTitle>
      <ResearchBlockText $textColor={textColor}>{text}</ResearchBlockText>
    </ResearchBlockStyled>
  )
}

const ResearchSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`
const FirstResearchSection = styled.div`
  padding: 120px 64px 80px;
  display: flex;
  flex-direction: column;
  gap: 80px;
  align-items: center;
  justify-content: center;
  @media (max-width: ${breakpoints.desktop}px) {
    padding: 80px 24px 40px;
  }
`
const ResearchSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: center;
  max-width: 976px;
`
const ResearchTitle = styled.h1`
  @media (max-width: ${breakpoints.desktop}px) {
    font-size: 40px;
  }
`
const ResearchBlockContainer = styled.div`
  display: flex;
  gap: 32px;
  @media (max-width: ${breakpoints.desktop}px) {
    flex-direction: column;
    gap: 24px;
  }
`
const ResearchBlocks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 50%;
  @media (max-width: ${breakpoints.desktop}px) {
    gap: 24px;
    width: 100%;
  }
`
const ResearchImage = styled.img`
  width: 100%;
  border-radius: 24px;
`
const ResearchBlockStyled = styled.div.attrs<{
  $bg?: keyof DefaultTheme['colors']
  $grow?: boolean
}>((props) => ({
  $bg: props.$bg || 'secondary-600',
  $grow: props.$grow || false,
}))`
  width: 100%;
  border-radius: 24px;
  padding: 40px;
  background-color: ${(props) =>
    props.theme.colors[props.$bg as keyof DefaultTheme['colors']]};
  flex-grow: ${(props) => (props.$grow ? 1 : 0)};
  justify-content: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const ResearchBlockTitle = styled.h3.attrs<{
  $titleColor?: string
  $titleSize?: string
}>((props) => ({
  $titleColor: props.$titleColor || 'white',
  $titleSize: props.$titleSize || 'h3',
}))`
  color: ${(props) => props.$titleColor};
  font-size: ${(props) => (props.$titleSize === 'h1' ? '56px' : '40px')};
  @media (max-width: ${breakpoints.desktop}px) {
    font-size: 40px;
  }
`
const ResearchBlockText = styled.h6.attrs<{
  $textColor?: string
}>((props) => ({
  $textColor: props.$textColor || 'white',
}))`
  color: ${(props) => props.$textColor};
  // font-weight: semibold;
`
const SecondResearchSection = styled.div`
  width: 100%;
  padding: 140px 64px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-image: url('/images/research/Background.png');
  object-fit: contain;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: top;
`

export default ResearchPage
